import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { Calendar, ChartBar, Image, Clock, CheckCircle, XCircle, PaintBrushBroad } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useApi } from "@/api/useApi";
import { useRole } from "@/hooks/useRole";

const platformColors = {
  facebook: "#1877F2", instagram: "#E1306C", linkedin: "#0A66C2", tiktok: "#010101",
};

const statusBadge = (status) =>
  status === "approved" ? "bg-[#e7f6ec] text-[#15803d]" :
  status === "pending_review" ? "bg-[#fef7e0] text-[#a16207]" :
  "bg-[#eef1f2] text-[#3C4048]";

export default function Dashboard() {
  const navigate = useNavigate();
  const api = useApi();
  const { user } = useUser();
  const { canManageContent, canSeeAnalytics } = useRole();
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, scheduled: 0 });
  const [recentPosts, setRecentPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const postsRes = await api.get(`/posts`);
      const posts = postsRes.data;
      setStats({
        total: posts.length,
        pending: posts.filter((p) => p.status === "pending_review").length,
        approved: posts.filter((p) => p.status === "approved").length,
        scheduled: posts.filter((p) => p.status === "scheduled").length,
      });
      setRecentPosts(posts.slice(0, 6));
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#3C4048] text-xl">Loading…</div>
      </div>
    );
  }

  const cards = [
    { key: "total", label: "Total Posts", value: stats.total, icon: Calendar, color: "#3A7A8C", testid: "stat-card-total" },
    { key: "pending", label: "Pending Review", value: stats.pending, icon: Clock, color: "#a16207", testid: "stat-card-pending" },
    { key: "approved", label: "Approved", value: stats.approved, icon: CheckCircle, color: "#15803d", testid: "stat-card-approved" },
    { key: "scheduled", label: "Scheduled", value: stats.scheduled, icon: XCircle, color: "#5FA8B4", testid: "stat-card-scheduled" },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-container">
      <div>
        <h1 className="text-4xl font-bold text-[#3C4048] mb-2 tracking-tight" style={{ fontFamily: "Montserrat", fontWeight: 800 }}>
          Dashboard Overview
        </h1>
        <p className="text-[#7A9090]">Welcome back, {user?.firstName || "there"}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.key} className="bg-white border border-[#e5e7eb] rounded-[12px] p-6 hover:border-[#5FA8B4] transition-colors duration-200" data-testid={c.testid}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#7A9090] text-sm">{c.label}</span>
                <Icon size={20} weight="duotone" style={{ color: c.color }} />
              </div>
              <div className="text-3xl font-bold" style={{ fontFamily: "Montserrat", fontWeight: 800, color: c.color }}>{c.value}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#e5e7eb] rounded-[12px] p-6">
          <h2 className="text-xl font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Recent Posts</h2>
          <div className="space-y-3">
            {recentPosts.length === 0 ? (
              <p className="text-[#7A9090] text-center py-8">No posts yet</p>
            ) : (
              recentPosts.map((post) => (
                <div key={post.id} data-testid={`recent-post-${post.id}`} className="bg-[#fafafa] border border-[#e5e7eb] rounded-[12px] p-4 hover:border-[#5FA8B4] transition-colors duration-200">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-1 rounded text-white text-xs font-semibold" style={{ backgroundColor: platformColors[post.platform] || "#7A9090" }}>{post.platform}</span>
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${statusBadge(post.status)}`}>{post.status.replace("_", " ")}</span>
                  </div>
                  <p className="text-[#3C4048] text-sm line-clamp-3 whitespace-pre-line">{post.draft_text}</p>
                  <p className="text-[#7A9090] text-xs mt-2">Scheduled: {new Date(post.scheduled_date).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })}</p>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white border border-[#e5e7eb] rounded-[12px] p-6">
            <h3 className="text-lg font-bold text-[#3C4048] mb-4" style={{ fontFamily: "Montserrat", fontWeight: 700 }}>Quick Actions</h3>
            <div className="space-y-2">
              <QuickAction testid="quick-action-calendar" icon={Calendar} label="View Calendar" onClick={() => navigate("/calendar")} />
              {canManageContent && <QuickAction testid="quick-action-studio" icon={PaintBrushBroad} label="Design Studio" onClick={() => navigate("/studio")} />}
              <QuickAction testid="quick-action-media" icon={Image} label="Media Library" onClick={() => navigate("/media")} />
              {canSeeAnalytics && <QuickAction testid="quick-action-analytics" icon={ChartBar} label="View Analytics" onClick={() => navigate("/analytics")} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function QuickAction({ icon: Icon, label, onClick, testid }) {
  return (
    <Button data-testid={testid} onClick={onClick} className="w-full bg-white border border-[#e5e7eb] text-[#3C4048] hover:bg-[#f0f7f8] hover:border-[#5FA8B4] transition-colors duration-200 justify-start rounded-[12px]">
      <Icon size={18} className="mr-2 text-[#3A7A8C]" weight="regular" />
      {label}
    </Button>
  );
}
